import { HERO_CONTENT, ABOUT_TEXT, EXPERIENCES, PROJECTS, CONTACT, SOCIALS, STACK, YEARS_SHIPPING } from '../constants';

// One string per paragraph; Buffer hard-wraps them to the pane width at render time.
function wrap(text) {
    return text
        .split('\n')
        .map((p) => p.trim().replace(/\s+/g, ' '))
        .filter(Boolean)
        .flatMap((p, i) => (i ? ['', p] : [p]));
}

export function slugify(title) {
    return title
        .split('—')[0]
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function shortTitle(title) {
    return title.split('—')[0].trim();
}

function prettyUrl(url) {
    return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

const ALL_TOOLS = new Set(PROJECTS.flatMap((p) => p.technologies.map((t) => t.toLowerCase())));

export const STATS = {
    projects: PROJECTS.length,
    live: PROJECTS.filter((p) => p.link).length,
    years: YEARS_SHIPPING,
    since: new Date().getFullYear() - YEARS_SHIPPING,
    tools: ALL_TOOLS.size,
};

// Terminal rows (rendered without line numbers):
//   { t: 'prompt', path, cmd }        user@host path / › cmd
//   { t: 'kv', k, v, note, bar }      aligned key/value, optional 0..1 gauge
//   { t: 'links', items: [{label, href}] }
const prompt = (cmd, path = '~') => ({ t: 'prompt', path, cmd });

function projectBuffer(project) {
    const slug = slugify(project.title);
    const lines = [
        prompt(`fetch ${slug}`, '~/projects'),
        { t: 'kv', k: 'status', v: project.status, note: project.category },
        {
            t: 'kv',
            k: 'stack',
            v: project.technologies.length,
            bar: project.technologies.length / ALL_TOOLS.size,
            note: `${Math.round((project.technologies.length / ALL_TOOLS.size) * 100)}% of all tools`,
        },
        project.link
            ? { t: 'links', label: 'live', items: [{ label: prettyUrl(project.link), href: project.link }] }
            : { t: 'kv', k: 'live', v: '—', note: 'no public link' },
        prompt(`cat ${slug}.md`, '~/projects'),
        `# ${project.title}`,
        '',
    ];
    if (project.image) {
        lines.push({ img: project.image, alt: project.title, caption: `${slug}.png`, link: project.link });
        lines.push('');
    }
    lines.push('## Overview', '');
    lines.push(...wrap(project.description));
    lines.push('', '## Stack', '');
    lines.push(...wrap(project.technologies.map((t) => `\`${t}\``).join(' ')));
    lines.push('');
    if (project.link) {
        lines.push('## Links', '', `- [Visit ${prettyUrl(project.link)}](${project.link})`, '');
    }
    return {
        path: `projects/${slug}.md`,
        name: `${slug}.md`,
        label: `${slug}/`,
        title: shortTitle(project.title),
        lang: 'md',
        card: 'image',
        image: project.image,
        lines,
    };
}

function readme() {
    const [intro, more] = HERO_CONTENT.split('\n');
    const lines = [
        prompt('neofetch'),
        { t: 'neofetch' },
        prompt('whoami'),
        ...wrap(intro),
        '',
        ...wrap(more || ''),
        prompt('fetch adeboye'),
        { t: 'kv', k: 'projects', v: STATS.projects, note: `${STATS.live} live in production` },
        { t: 'kv', k: 'shipping', v: `${STATS.years}+ yrs`, note: `since ${STATS.since}` },
        { t: 'kv', k: 'tools', v: STATS.tools, note: 'across shipped projects · cat stack.json' },
        { t: 'kv', k: 'based', v: CONTACT.address.split(',')[0], note: CONTACT.address.split(',').slice(1).join(',').trim() },
        prompt('ls -l projects/'),
        {
            t: 'cards',
            items: PROJECTS.map((p) => ({
                label: slugify(p.title),
                href: `#/projects/${slugify(p.title)}.md`,
                image: p.image,
                note: `${p.category} · ${p.technologies.length} tools`,
                live: Boolean(p.link),
            })),
        },
        prompt('ls links/'),
        {
            t: 'links',
            items: [...SOCIALS.map((s) => ({ label: s.name, href: s.url })), { label: 'email', href: `mailto:${CONTACT.email}` }],
        },
        prompt('', '~'),
    ];
    return { path: 'README.md', name: 'README.md', label: 'whoami', title: 'whoami', lang: 'term', card: 'brand', lines };
}

function about() {
    return {
        path: 'about.md',
        name: 'about.md',
        label: 'about/',
        title: 'About',
        lang: 'md',
        card: 'text',
        lines: [prompt('cat about.md'), '# About', '', ...wrap(ABOUT_TEXT), ''],
    };
}

function experience() {
    const lines = [prompt('git log --graph experience/')];
    EXPERIENCES.forEach((exp) => {
        lines.push({ t: 'commit', year: exp.year, role: exp.role, company: exp.company, text: exp.description, tags: exp.technologies });
    });
    lines.push(prompt('', '~/experience'));
    return { path: 'experience.md', name: 'experience.md', label: 'experience/', title: 'Experience', lang: 'term', card: 'timeline', lines };
}

// Tools ranked by how many shipped projects use them ("React 19" counts as React).
export function topTools(limit = 10) {
    const counts = new Map();
    PROJECTS.forEach((p) => {
        new Set(p.technologies.map((t) => t.replace(/\s+v?\d+(\.\d+)*$/i, ''))).forEach((name) => {
            const key = name.toLowerCase();
            const entry = counts.get(key) || { name, count: 0 };
            entry.count += 1;
            counts.set(key, entry);
        });
    });
    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, limit);
}

function stack() {
    const top = topTools(10);
    const max = top[0]?.count || 1;
    return {
        path: 'stack.json',
        name: 'stack.json',
        label: 'stack/',
        title: 'Stack',
        lang: 'json',
        card: 'grid',
        lines: [
            prompt('stack --top 10'),
            ...top.map((t) => ({
                t: 'kv',
                k: t.name,
                v: t.count,
                bar: t.count / max,
                note: `of ${PROJECTS.length} projects`,
                wide: true,
            })),
            prompt('cat stack.json | jq'),
            ...JSON.stringify(STACK, null, 2).split('\n'),
        ],
    };
}

function contact() {
    const tel = CONTACT.phoneNo.replace(/\s+/g, '');
    return {
        path: 'contact.md',
        name: 'contact.md',
        label: 'contact/',
        title: 'Contact',
        lang: 'md',
        card: 'contact',
        lines: [
            prompt('cat contact.md'),
            '# Contact',
            '',
            ...wrap('Open to freelance projects, full-time roles and interesting problems. The fastest way to reach me is email.'),
            '',
            `- email    [${CONTACT.email}](mailto:${CONTACT.email}?subject=Project%20Inquiry)`,
            `- phone    [${CONTACT.phoneNo}](tel:${tel})`,
            `- location ${CONTACT.address}`,
            '',
            '## Elsewhere',
            '',
            ...SOCIALS.map((s) => `- ${s.name.padEnd(9)}[${prettyUrl(s.url).split('?')[0]}](${s.url})`),
            '',
        ],
    };
}

function help() {
    return {
        path: 'help.txt',
        name: 'help.txt',
        title: 'Help',
        lang: 'md',
        hidden: true,
        lines: [
            '# Help',
            '',
            '## Keys',
            '',
            '- `j` / `k`      next / previous file',
            '- `gg` / `G`     top / bottom of the buffer',
            '- `ctrl-d/u`     scroll half a page',
            '- `:`            command line · `tab` completes · `↑↓` history',
            '- `esc`          cancel',
            '',
            '## Commands',
            '',
            '- `:e <file>`    open a file, e.g. `:e ura`',
            '- `:theme`       cycle themes, or `:theme <name>`',
            '- `:themes`      list themes',
            '- `:set rnu`     relative line numbers (from the line under your mouse)',
            '- `:set crt`     scanline monitor mode · `:set nocrt` to turn off',
            '- `:neofetch`    system info for this session',
            '- `:q`           back to the intro',
            '- `:help`        this file',
            '',
        ],
    };
}

const PROJECT_BUFFERS = PROJECTS.map(projectBuffer);

export const BUFFERS = [readme(), about(), experience(), stack(), contact(), ...PROJECT_BUFFERS, help()];

// Sidebar order: top-level files, then the projects folder.
export const TREE = [
    { type: 'dir', name: '~/adeboye' },
    ...BUFFERS.filter((b) => !b.hidden && !b.path.includes('/')).map((b) => ({ type: 'file', buffer: b, depth: 1 })),
    { type: 'dir', name: 'projects/', depth: 1 },
    ...PROJECT_BUFFERS.map((b) => ({ type: 'file', buffer: b, depth: 2 })),
];

export const OPENABLE = TREE.filter((n) => n.type === 'file').map((n) => n.buffer);

export function findBuffer(query) {
    if (!query) return null;
    const q = query.replace(/^\.?\/+/, '').toLowerCase();
    return (
        BUFFERS.find((b) => b.path.toLowerCase() === q) ||
        BUFFERS.find((b) => b.name.toLowerCase() === q) ||
        BUFFERS.find((b) => b.path.toLowerCase().startsWith(q)) ||
        BUFFERS.find((b) => b.path.toLowerCase().includes(q)) ||
        null
    );
}

// Source-looking text for the intro wall, built from the real project data.
export function wallSource() {
    const parts = PROJECTS.map((p) => {
        const slug = slugify(p.title);
        return [
            `export const ${slug.replace(/-(\w)/g, (_, c) => c.toUpperCase())} = {`,
            `  title: "${shortTitle(p.title)}",`,
            `  category: "${p.category}",`,
            `  status: "${p.status}",`,
            p.link ? `  link: "${p.link}",` : `  link: null,`,
            `  stack: [${p.technologies.map((t) => `"${t}"`).join(', ')}],`,
            `};`,
            '',
            `function render${slug.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase())}() {`,
            `  return ship(${slug.replace(/-(\w)/g, (_, c) => c.toUpperCase())}, { tested: true });`,
            `}`,
            '',
        ].join('\n');
    });
    const stackLines = JSON.stringify(STACK, null, 2);
    return [
        `// adeboye bello — full-stack software engineer`,
        `import { ship } from "./craft";`,
        '',
        ...parts,
        `export const stack = ${stackLines};`,
        '',
    ].join('\n');
}
